import { useState } from 'react'
import { Space, Typography, Row, Col, Card as CardAntd, Form, notification, Spin } from 'antd'
import classNames from 'classnames'
import _ from 'lodash'
import update from 'immutability-helper'
import { useAsync } from 'react-use'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Select from 'components/Select'
import AllActiveInactive from 'components/AllActiveInactive'
import SearchOrganization from 'components/SearchOrganization'
import FilterRoleSelect from 'components/FilterRoleSelect'
import FilterProfileSelect from 'components/FilterProfileSelect'

import { useTranslate } from 'hooks/translate'

import { getUserListApi, updateUserByIdApi, importUserLDAPApi } from 'services/user'

import { convertOrganizationalStructureToFilters } from 'utils/organization'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function PageUser () {
  const { t } = useTranslate()

  const [editMode, setEditMode] = useState(false)
  const [showMassAssignProfileModal, setShowMassAssignProfileModal] = useState(false)
  const [oldDataSource, setOldDataSource] = useState([])
  const [currentDataSource, setCurrentDataSource] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    profile_id: null,
    role_id: null,
    employee_name: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive
  const [dataForUpdate, setDataForUpdate] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [orgFilters, setOrgFilters] = useState({
    company_id: null,
    group_id: null,
    bu_id: null,
    division_id: null,
    department_id: null,
    section_id: null,
  })

  const _list = async () => {
    if (loading) return

    const params = {
      ...filters,
      ...orgFilters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getUserListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setOldDataSource(res.data?.items)
    setCurrentDataSource(res.data?.items)
  }

  useAsync(async () => {
    _list()
  }, [status, filters, orgFilters])

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('Company/Group/BUSBU/Division/Department'),
      dataIndex: 'organization',
      key: 'organization',
      sorter: (a, b) => a.organization.localeCompare(b.organization),
      width: '30%',
    },
    {
      title: t('Profile'),
      dataIndex: 'profile',
      key: 'profile',
      sorter: (a, b) => a.profile.profile_name.localeCompare(b.profile.profile_name),
      render: (text, record, index) => {
        return (
          <FilterProfileSelect
            disabled={!editMode || !record?.canEdit}
            value={text}
            onChange={(value) => {
              const updatedProfile = update(record, {
                profile: { $set: value },
              })

              const newCurrentDataSource = update(currentDataSource, {
                $splice: [[index, 1, updatedProfile]],
              })

              setCurrentDataSource(newCurrentDataSource)

              selectProfile({ value, record })
            }}
          />
        )
      },
    },
    {
      title: t('Role'),
      dataIndex: 'role',
      key: 'role',
      sorter: (a, b) => a.role.localeCompare(b.role),
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      sorter: (a, b) => a.status.label.localeCompare(b.status.label),
      render: (text, record, index) => {
        if (editMode) {
          return (
            <Select
              options={[
                { label: t('Active'), value: true },
                { label: t('Inactive'), value: false },
              ]}
              // value={record?.is_active}
              defaultValue={record?.status?.value}
              onChange={(value) => {
                const field = 'status'
                const key = record?.key

                const index = _.findIndex(currentDataSource, { key })

                const updatedDataSource = update(currentDataSource[index], {
                  [field]: { $set: value },
                })

                const newCurrentDataSource = update(currentDataSource, {
                  $splice: [[index, 1, updatedDataSource]],
                })

                setCurrentDataSource(newCurrentDataSource)

                selectStatus({ value, record })
              }}
              style={{ width: '100%' }}
            />
          )
        }
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
  ]

  const getChangeMessages = () => {
    // const result = _.compact(
    //   _.map(currentDataSource, (currentData, index) => {
    //     const oldData = oldDataSource[index]
    //     if (!_.isEqual(currentData, oldData)) {
    //       const oldProfile = oldData?.profile || 'None'
    //       const newProfile = currentData?.profile

    //       const name = currentData?.name

    //       return `${name}:Form ${oldProfile} to ${newProfile} `
    //     }

    //     return null
    //   })
    // )

    const result = ''

    return result
  }

  const changeMessages = getChangeMessages()

  const selectedDataSource = _.filter(currentDataSource, (currentData) => {
    return _.includes(selectedRowKeys, currentData.key)
  })

  const selectProfile = ({ value, record }) => {
    const newDataForUpdate = { ...dataForUpdate }

    newDataForUpdate[record.key] = {
      user_id: record.id,
      profile_id: value,
      status: record?.status?.value,
      record,
    }

    setDataForUpdate(newDataForUpdate)
  }

  const selectStatus = ({ value, record }) => {
    const newDataForUpdate = { ...dataForUpdate }

    newDataForUpdate[record.key] = {
      user_id: record.id,
      profile_id: record?.profile,
      status: value,
      record,
    }

    setDataForUpdate(newDataForUpdate)
  }

  const importUserLDAP = async () => {
    const res = await importUserLDAPApi()

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    } else {
      notification.success({
        message: 'Success',
        description: res.data.message,
      })

      _list()
    }
  }

  return (
    <Styled className='page page-user'>
      <Page
        title={t('User and Role')}
        searchPlaceholder={t('Search by name')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            employee_name: value,
            page: 1,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Form
            layout='vertical'
            name='search'
            onFinish={(values) => {
              const nextOrgFilters = convertOrganizationalStructureToFilters(values.search)
              setOrgFilters({
                ...nextOrgFilters,
              })
            }}
          >
            <Row gutter={8}>
              <Col span={20}>
                <Form.Item
                  label={t('Organizational Structure')}
                  name='search'
                  style={{ marginBottom: 0 }}
                >
                  <SearchOrganization />
                </Form.Item>
              </Col>

              <Col>
                <Form.Item label='&nbsp;' style={{ marginBottom: 0 }}>
                  <Button type='primary' htmlType='submit'>
                    {t('Search')}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Row justify='space-between' align='middle'>
            <Col>
              <Space size='middle'>
                <FilterRoleSelect
                  onChange={(value) => {
                    setFilters({
                      ...filters,
                      role_id: value,
                      page: 1,
                    })
                  }}
                />
                <FilterProfileSelect
                  onChange={(value) => {
                    setFilters({
                      ...filters,
                      profile_id: value,
                      page: 1,
                    })
                  }}
                />
              </Space>
            </Col>
            <Col>
              <Space size='middle'>
                {!_.isEmpty(selectedRowKeys) && (
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => setShowMassAssignProfileModal(true)}
                  >
                    {t('Mass Assign Profile')}
                  </Button>
                )}
                <Button type='primary' size='large' onClick={() => importUserLDAP()}>
                  {t('Import User')}
                </Button>
              </Space>
            </Col>
          </Row>

          <Card
            title={t('Users')}
            titleExtra={
              editMode ? (
                <Space>
                  <Button
                    type='primary'
                    onClick={() => {
                      setShowConfirmModal(true)
                    }}
                  >
                    {t('Save')}
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedRowKeys([])
                      setCurrentDataSource(oldDataSource)
                      setEditMode(false)
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                </Space>
              ) : (
                <Button type='primary' onClick={() => setEditMode(true)}>
                  {t('Edit Mode')}
                </Button>
              )
            }
          >
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={loading}
              rowSelection={{
                selectedRowKeys,
                onChange: (value) => {
                  setSelectedRowKeys(value)
                },
                getCheckboxProps: (record) => {
                  return {
                    disabled: !editMode || !record?.canEdit,
                  }
                },
              }}
              columns={columns}
              dataSource={currentDataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 1200 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={800}
        visible={showMassAssignProfileModal}
        title={t('Mass Assign Profile')}
        onCancel={() => {
          setShowMassAssignProfileModal(false)
        }}
        footer={[
          <Button
            loading={submitting}
            key='save'
            type='primary'
            disabled={!selectedProfile}
            onClick={async () => {
              // const newCurrentDataSource = _.map(currentDataSource, (currentData) => {
              //   return {
              //     ...currentData,
              //     profile: _.includes(selectedRowKeys, currentData.key)
              //       ? selectedProfile
              //       : currentData.profile,
              //   }
              // })

              // setCurrentDataSource(newCurrentDataSource)
              // setSelectedRowKeys([])
              // setShowMassAssignProfileModal(false)

              if (submitting) return

              setSubmitting(true)

              await Promise.all(
                _.map(selectedDataSource, async (v) => {
                  const res = await updateUserByIdApi({
                    user_id: v.id,
                    profile_id: selectedProfile,
                  })

                  if (res.status === 'error') {
                    notification.error({
                      message: 'Failed',
                      description: res.message,
                    })

                    return
                  }

                  return res
                })
              )

              setSubmitting(false)

              _list()

              setEditMode(false)
              setDataForUpdate({})
              setSelectedProfile(null)
              setSelectedRowKeys([])
              setShowMassAssignProfileModal(false)
            }}
          >
            {t('Save')}
          </Button>,
        ]}
      >
        <div>
          <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <CardAntd>
              <Table
                columns={[
                  {
                    title: t('Name'),
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: t('Company/Group/BUSBU/Division/Department'),
                    dataIndex: 'organization',
                    key: 'organization',
                  },
                ]}
                dataSource={selectedDataSource}
                pagination={false}
              />
            </CardAntd>
            <Space>
              <Typography.Text>{t('Select Profile')}</Typography.Text>
              <FilterProfileSelect
                onChange={(value) => {
                  setSelectedProfile(value)
                }}
              />
            </Space>
          </Space>
        </div>
      </Modal>

      <Modal
        visible={showConfirmModal}
        title={t('Confirm changes')}
        onCancel={() => {
          setShowConfirmModal(false)
        }}
        footer={[
          <Button
            loading={submitting}
            key='save'
            type='primary'
            onClick={async () => {
              if (submitting) return

              setSubmitting(true)

              await Promise.all(
                _.map(dataForUpdate, async (v) => {
                  const res = await updateUserByIdApi({
                    user_id: v.user_id,
                    profile_id: v.profile_id,
                    is_active: v.status,
                  })

                  if (res.status === 'error') {
                    notification.error({
                      message: 'Failed',
                      description: res.message,
                    })

                    return
                  }

                  return res
                })
              )

              setSubmitting(false)

              _list()

              setEditMode(false)
              setShowConfirmModal(false)
              setDataForUpdate({})
            }}
          >
            {t('Save')}
          </Button>,
        ]}
      >
        <div>
          <Typography.Text>{t('You have change User Profile as below')}</Typography.Text>
          <Typography.Paragraph>
            <ol>
              {_.map(changeMessages, (changeMessage, index) => {
                return (
                  <li key={index}>
                    <Typography.Text>{changeMessage}</Typography.Text>
                  </li>
                )
              })}
            </ol>
          </Typography.Paragraph>
        </div>
      </Modal>
    </Styled>
  )
}
